/**
 * 
 * Plugin for Microsoft Gateway API Login
 * running with @azure/msal-browser: ^4.12.0
 * 
 */

import { PublicClientApplication, InteractionRequiredAuthError  } from '@azure/msal-browser'

export default defineNuxtPlugin(async (nuxtApp) => {
    const runtimeConfig = useRuntimeConfig()

    const config = {
        auth:
        {
            clientId: '3dd8a014-0dfc-4f86-a079-ea515acb714c',
            authority: 'https://login.microsoftonline.com/organizations/',
            redirectUri: runtimeConfig.public.redirectUri,
        },
        cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false
        }
    }

    const msal = new PublicClientApplication(config)
    await msal.initialize()

    try {
        const response = await msal.handleRedirectPromise();
        if (response) {
            // Eine Redirect-Antwort wurde verarbeitet (z.B. nach loginRedirect)
            // Das Konto ist nun im Cache und als aktives Konto gesetzt (falls es das erste ist)
            console.log('MSAL: Redirect-Antwort verarbeitet. Angemeldetes Konto:', response.account?.username);

            if (response.account) {
                msal.setActiveAccount(response.account);
            }
        } else {
            // Keine Redirect-Antwort, d.h. die Seite wurde direkt aufgerufen
            console.log('MSAL: Keine Redirect-Antwort. Prüfe bestehende Session.');
        }
    } catch (error) {
        console.error('MSAL: Fehler bei handleRedirectPromise:', error);
        // Behandle Fehler bei der Redirect-Verarbeitung, z.B. wenn der Hash ungültig ist
    }

    const login = async () => {
        let loginRequest = {
            scopes: ['user.read'],
        }
        try {
            let loginResponse = await msal.loginPopup(loginRequest)

            if (loginResponse.account) {
                msal.setActiveAccount(loginResponse.account);
            }

            return loginResponse
        }
        catch (err) {
            console.log(err);
        }
    }

    let tokenResponse

    const acquireTokenSilent = async () => {
        const accounts = msal.getAllAccounts()

        if (accounts.length === 0) {
            console.log('MSAL: acquireTokenSilent: Kein Konto im Cache gefunden.');
            return null; // Kein angemeldetes Konto
        }

        const activeAccount = msal.getActiveAccount() || accounts[0];

        let tokenRequest = {
            scopes: ['user.read'],
            account: activeAccount
        }

        try {
            const tokenResponse = await msal.acquireTokenSilent(tokenRequest);
            console.log('MSAL: acquireTokenSilent: Token erfolgreich erhalten.');
            return tokenResponse;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Stille Erneuerung fehlgeschlagen. Interaktion ist erforderlich.
                console.warn('MSAL: acquireTokenSilent: Interaktion erforderlich. Benutzer muss sich erneut anmelden.', error);
                // HIER NICHT SOFORT loginPopup/loginRedirect aufrufen, da dies außerhalb einer Benutzeraktion geschieht.
                // Stattdessen sollten Sie null zurückgeben oder einen Fehler werfen,
                // und die aufrufende Komponente entscheidet, ob ein interaktiver Login ausgelöst wird.
                return null;
            } else {
                console.error('MSAL: acquireTokenSilent: Unbekannter Fehler beim Abrufen des Tokens:', error);
                throw error;
            }
        }
    }

    const logout = async () => {
        const currentAccount = msal.getActiveAccount();

        if (!currentAccount) {
            console.log('MSAL: Kein aktives Konto zum Abmelden gefunden.');
            // Hier könnten Sie auch ein logoutRedirect() aufrufen, um sicherzustellen, dass die Azure AD Session beendet wird
            await msal.logoutPopup(); // Oder logoutRedirect() wenn keine Popup-Einschränkungen bestehen
            return;
        }

        try {
            await msal.logoutPopup({
                account: currentAccount,
                // postLogoutRedirectUri: runtimeConfig.public.postLogoutRedirectUri // Optional: Wenn Sie eine spezifische Umleitung nach dem Logout wünschen
            });
            console.log('MSAL: Erfolgreich abgemeldet.');
        } catch (err) {
            console.error('MSAL: Fehler beim Logout:', err);
            // Fallback für Logout, z.B. wenn Popup blockiert wird
            await msal.logoutRedirect({
                account: currentAccount,
                // postLogoutRedirectUri: runtimeConfig.public.postLogoutRedirectUri
            });
        }
    }

    const checkAndGetToken = async () => {
        const currentAccount = msal.getActiveAccount();
        
        if (currentAccount) {
            // console.log('MSAL: checkAndGetToken: Aktives Konto gefunden, versuche stilles Token.');

            try {
                const response = await acquireTokenSilent();
                
                if (response) {
                    // console.log('MSAL: checkAndGetToken: Still angemeldet und Token erhalten.');
                    return response;
                }
            } catch (error) {
                // console.error('MSAL: checkAndGetToken: Fehler beim stillen Token-Abruf:', error);
                // Hier könnten Sie den Benutzer auffordern, sich erneut anzumelden, wenn InteractionRequiredAuthError auftritt.
                // Z.B. durch Setzen eines Status in Pinia/Vuex und Weiterleitung zur Login-Seite.
            }
        }

        console.log('MSAL: checkAndGetToken: Kein aktives Konto oder stilles Token nicht möglich.');
        return null;
    }

    return {
        provide: {
            login,
            acquireTokenSilent,
            logout,
            checkAndGetToken 
        }
    }
})